import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./App.module.css";
import PortalHeader from "./components/PortalHeader";
import { useTheme } from "./useTheme";
import PortalFooter from "./components/PortalFooter";
import LeaderboardPage from "./pages/LeaderboardPage";
import PortfolioPage from "./pages/PortfolioPage";
import ReferralsPage from "./pages/ReferralsPage";
import StatsPage from "./pages/StatsPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutStatusPage from "./pages/CheckoutStatusPage";
import AccountPage from "./pages/AccountPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import useSupportTickets from "./useSupportTickets";
import DevStateSwitcher from "./components/DevStateSwitcher";
import KycModal from "./components/KycModal";
import { useKyc } from "./useKyc";
import { useLanguage } from "./LanguageContext";
import { API_URL, useAuth } from "./AuthContext";

const routePaths = ["/portfolio", "/stats", "/leaderboard", "/referrals"];
const routeKeys = ["nav_portfolio", "nav_stats", "nav_leaderboard", "nav_referrals"];
const allValidPaths = [...routePaths, "/account", "/help"];

const PAGE_NAMES = {
  "/portfolio": "Portfolio",
  "/stats": "Stats",
  "/leaderboard": "Leaderboard",
  "/referrals": "Referrals",
  "/account": "Account",
  "/help": "Help center",
};

const CHECKOUT_STATUS_PATH = /^\/checkout\/status\/[0-9a-f]{24}$/i;

// free → trading (started free account) → qualified (hit +1%, awaiting activation;
//   qualified-24h: under 24h left, urgent gold banner; expired: window ran out, start a new free attempt)
// (trading → failed: breached max drawdown, free retry restarts trading
//  → exhausted: breached again, no retries left; can still buy activation outright)
// → activated (paid, KYC pending) → verified (KYC done, can withdraw 1%)
// → funded (first withdrawal paid out, fresh funded account) → established (trading + monthly payouts)
// → breached (funded cycle ended on drawdown/daily loss; can buy a new account)
const ACCOUNT_STATUSES = [
  "free",
  "trading",
  "failed",
  "exhausted",
  "qualified",
  "qualified-24h",
  "expired",
  "activated",
  "verified",
  "funded",
  "established",
  "breached",
];
// Statuses a trader passes through before paying the activation fee
const PRE_ACTIVATION_STATUSES = ACCOUNT_STATUSES.slice(0, ACCOUNT_STATUSES.indexOf("activated"));
// The help center (support tickets) is only open to traders who have paid for activation,
// including after a funded cycle ends (breached)
const HELP_CENTER_STATUSES = ACCOUNT_STATUSES.slice(ACCOUNT_STATUSES.indexOf("activated"));
// Funded payout button: eligible → opens the payout modal; before-date → hidden;
// below-min → disabled with a minimum-amount tooltip
const PAYOUT_STATES = ["eligible", "before-date", "below-min"];
// Activated KYC card: "required" (start verification), "pending" (in review),
// "failed" (rejected, can retry) or "rejected" (final, contact support only)
const KYC_STATES = ["required", "pending", "failed", "rejected"];
// Sumsub status from the API → KYC card state ("verified" moves the account on instead)
const KYC_CARD_STATE = { none: "required", init: "required", pending: "pending", retry: "failed", rejected: "rejected" };
// Failed/exhausted preview: which rules the banner names (a live account uses its real ones)
const BREACH_PREVIEWS = {
  drawdown: ["max_drawdown"],
  daily: ["daily_loss"],
  inactivity: ["inactivity"],
  both: ["max_drawdown", "daily_loss"],
  all: ["max_drawdown", "daily_loss", "inactivity"],
};
const ACTIVATION_WINDOW_MS = 48 * 60 * 60 * 1000;
// Accounts are pre-provisioned, so the claim comes back almost instantly. Hold the
// "Getting your account ready..." screen at least this long so it reads as setup
// rather than a flicker.
const MIN_ACCOUNT_SETUP_MS = 3500;
// How often a trader waiting for a trading account checks whether one was assigned
const WAITLIST_POLL_MS = 30 * 1000;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// Qualified previews differ only in how much of the activation window is left
const ACTIVATION_TIME_LEFT = {
  qualified: ACTIVATION_WINDOW_MS,
  "qualified-24h": (23 * 60 + 10) * 60 * 1000 + 28 * 1000,
  expired: -(25 * 60 * 60 * 1000),
};
// Account-state simulator (switcher + `?state=`): always on locally; on other builds
// only when REACT_APP_SHOW_STATE_SWITCHER=true is set at build time (e.g. staging)
const SHOW_STATE_SWITCHER =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_SHOW_STATE_SWITCHER === "true";

// `?state=qualified` (etc.) sets the starting account status
function getInitialAccountStatus() {
  if (!SHOW_STATE_SWITCHER) return "free";
  const state = new URLSearchParams(window.location.search).get("state");
  return ACCOUNT_STATUSES.includes(state) ? state : "free";
}

function getInitialPayoutState() {
  if (!SHOW_STATE_SWITCHER) return "eligible";
  const state = new URLSearchParams(window.location.search).get("payout");
  return PAYOUT_STATES.includes(state) ? state : "eligible";
}

// `?kyc=failed` (etc.) previews a KYC card state; otherwise the real status is shown
function getInitialKycOverride() {
  if (!SHOW_STATE_SWITCHER) return null;
  const state = new URLSearchParams(window.location.search).get("kyc");
  return KYC_STATES.includes(state) ? state : null;
}

function getInitialBreachPreview() {
  const preview = SHOW_STATE_SWITCHER && new URLSearchParams(window.location.search).get("breach");
  return preview in BREACH_PREVIEWS ? preview : "drawdown";
}

function normalizePath(pathname) {
  if (!pathname || pathname === "/") {
    return "/portfolio";
  }

  const normalized = pathname.endsWith("/") && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;

  // Help center sections/articles (/help/<category>[/<article>]) are resolved by the page itself
  if (normalized.startsWith("/help/")) return normalized;
  // A crypto checkout's status page: /checkout/status/<checkout id>
  if (CHECKOUT_STATUS_PATH.test(normalized)) return normalized;

  return allValidPaths.includes(normalized) ? normalized : "/portfolio";
}

function usePortalRoute() {
  const [pathname, setPathname] = useState(() => normalizePath(window.location.pathname));

  useEffect(() => {
    const handlePopState = () => {
      setPathname(normalizePath(window.location.pathname));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (nextPath) => {
    const normalized = normalizePath(nextPath);

    if (normalized === pathname) {
      return;
    }

    window.history.pushState({}, "", normalized);
    setPathname(normalized);
  };

  return { pathname, navigate };
}

export default function App() {
  const { pathname, navigate } = usePortalRoute();
  // Help center subpages share the /help nav item, page title and component
  const isHelpPath = pathname === "/help" || pathname.startsWith("/help/");
  const basePath = isHelpPath ? "/help" : pathname;
  const { t } = useLanguage();
  const { token, user } = useAuth();
  const { effectiveTheme, toggleTheme } = useTheme();
  const [accountStatus, setAccountStatus] = useState(getInitialAccountStatus);
  // The trader's MT5 account from the free pool (null until they press START TRADING)
  const [tradingAccount, setTradingAccount] = useState(null);
  // Pressed START TRADING (or FREE RETRY) while the pool was empty: the API's waitlist
  // job assigns them an account once one is added
  const [waitingForAccount, setWaitingForAccount] = useState(false);
  const [payoutState, setPayoutState] = useState(getInitialPayoutState);
  const kyc = useKyc();
  const [kycOverride, setKycOverride] = useState(getInitialKycOverride);
  const kycState = kycOverride ?? KYC_CARD_STATE[kyc.status] ?? "required";
  const [kycOpen, setKycOpen] = useState(false);
  const [breachPreview, setBreachPreview] = useState(getInitialBreachPreview);
  // Category the help center opens its report modal with on arrival (undefined = don't open)
  const [pendingReport, setPendingReport] = useState(undefined);
  // null | "funded" (original $250 checkout) | "activation" (post-qualification $599)
  // | "instant" ($599 activation without qualifying, after free retries run out)
  const [checkoutMode, setCheckoutMode] = useState(null);
  // The qualified previews all render as "qualified"; only the deadline differs
  const portfolioStatus = accountStatus in ACTIVATION_TIME_LEFT ? "qualified" : accountStatus;
  // Stand-in for the backend's qualification timestamp + 48h
  const activationDeadline = useMemo(
    () => Date.now() + (ACTIVATION_TIME_LEFT[accountStatus] ?? ACTIVATION_WINDOW_MS),
    [accountStatus]
  );
  const isFunded = accountStatus !== "free";
  const hasHelpCenter = HELP_CENTER_STATUSES.includes(accountStatus);
  // Support tickets load here so they survive leaving the help center
  const support = useSupportTickets(hasHelpCenter);
  const routes = [
    ...routePaths.map((path, i) => ({ path, label: t(routeKeys[i]) })),
    ...(hasHelpCenter ? [{ path: "/help", label: t("nav_help") }] : []),
  ];

  const handleGetFunded = useCallback(() => setCheckoutMode("funded"), []);
  const handleActivate = useCallback(() => setCheckoutMode("activation"), []);
  const handleBuyActivation = useCallback(() => setCheckoutMode("instant"), []);
  // Claims an account from the pool (or gets back the one already held). Rejects with a
  // message the start card can show; the page keeps its loading screen until this settles.
  const handleStartTrading = useCallback(async () => {
    const request = fetch(`${API_URL}/api/trading-account/start`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.code === "POOL_EMPTY") setWaitingForAccount(true);
        const error = new Error(data.error || "We couldn't set up your account. Please try again.");
        error.code = data.code;
        throw error;
      }
      return data.account;
    });
    const [result] = await Promise.allSettled([request, wait(MIN_ACCOUNT_SETUP_MS)]);
    if (result.status === "rejected") throw result.reason;
    setTradingAccount(result.value);
    setWaitingForAccount(false);
    setAccountStatus("trading");
  }, [token]);
  const handleVerify = useCallback(() => setKycOpen(true), []);
  const { refresh: refreshKyc } = kyc;
  const handleKycClose = useCallback(() => {
    setKycOpen(false);
    refreshKyc();
  }, [refreshKyc]);

  // A passed Sumsub check unlocks the first withdrawal (unless the KYC switcher is
  // previewing a card state)
  useEffect(() => {
    if (kyc.status === "verified" && !kycOverride) {
      setAccountStatus((status) => (status === "activated" ? "verified" : status));
    }
  }, [kyc.status, kycOverride, accountStatus]);
  const handleContactSupport = useCallback(() => {
    setPendingReport("KYC");
    navigate("/help");
  }, [navigate]);
  const clearPendingReport = useCallback(() => setPendingReport(undefined), []);
  const handleCheckoutBack = () => setCheckoutMode(null);
  const handleCheckoutComplete = () => {
    if (checkoutMode === "activation" || checkoutMode === "instant") {
      setAccountStatus("activated");
      navigate("/portfolio");
    } else {
      // A paid funded account is set up by hand, so the trader's status doesn't change yet
      navigate("/portfolio");
    }
    setCheckoutMode(null);
  };

  // Leaderboard and Stats invite free users to start a free account instead of buying one
  const startFree = { button: t("lb_cta_btn"), onClick: () => navigate("/portfolio") };
  const footerCtas = {
    "/leaderboard": {
      ...startFree,
      title: t("lb_cta_title"),
      highlight: t("lb_cta_highlight"),
      desc: t("lb_cta_desc"),
      mobileDesc: t("lb_cta_desc_mobile"),
    },
    "/stats": {
      ...startFree,
      title: t("stats_cta_title"),
      highlight: "",
      desc: t("lb_cta_desc"),
    },
  };
  const footerCta = footerCtas[pathname];

  // A trader who already started comes back to their account, not the START TRADING card.
  // While they're waiting for one, this re-checks every WAITLIST_POLL_MS so the account
  // shows up as soon as the API's waitlist job assigns it.
  useEffect(() => {
    let controller;
    const load = () => {
      controller?.abort();
      controller = new AbortController();
      fetch(`${API_URL}/api/trading-account`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data) return;
          setWaitingForAccount(Boolean(data.waiting));
          if (!data.account) return;
          setTradingAccount(data.account);
          setAccountStatus((status) => (status === "free" ? "trading" : status));
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error("Failed to load trading account:", err);
        });
    };
    load();
    const timer = waitingForAccount ? setInterval(load, WAITLIST_POLL_MS) : null;
    return () => {
      clearInterval(timer);
      controller?.abort();
    };
  }, [token, waitingForAccount]);

  // A trader who paid the activation fee (recorded by the Whop webhook) comes back activated
  useEffect(() => {
    if (!user?.activated) return;
    setAccountStatus((status) => (PRE_ACTIVATION_STATUSES.includes(status) ? "activated" : status));
  }, [user]);

  // Leaving a paid state (e.g. via the state switcher) closes the help center
  useEffect(() => {
    if (isHelpPath && !hasHelpCenter) navigate("/portfolio");
  }, [isHelpPath, hasHelpCenter, navigate]);

  useEffect(() => {
    const name = PAGE_NAMES[basePath];
    document.title = name ? `${name} | Prop Funding Portal` : "Prop Funding Portal";
  }, [basePath]);

  const page = useMemo(() => {
    switch (basePath) {
      case "/portfolio":
        return (
          <PortfolioPage
            accountStatus={portfolioStatus}
            payoutState={payoutState}
            kycState={kycState}
            tradingAccount={tradingAccount}
            waitingForAccount={waitingForAccount}
            breachPreview={BREACH_PREVIEWS[breachPreview]}
            onStartTrading={handleStartTrading}
            activationDeadline={activationDeadline}
            onActivate={handleActivate}
            onBuyActivation={handleBuyActivation}
            onGetFunded={handleGetFunded}
            onVerify={handleVerify}
            onContactSupport={handleContactSupport}
          />
        );
      case "/leaderboard":
        return <LeaderboardPage enrolled={isFunded} />;
      case "/referrals":
        return <ReferralsPage />;
      case "/account":
        return <AccountPage />;
      case "/help":
        return hasHelpCenter ? (
          <HelpCenterPage
            subPath={pathname.slice("/help".length)}
            onNavigate={navigate}
            support={support}
            pendingReport={pendingReport}
            onReportOpened={clearPendingReport}
          />
        ) : null;
      case "/stats":
      default:
        return <StatsPage />;
    }
  }, [
    pathname,
    portfolioStatus,
    payoutState,
    kycState,
    tradingAccount,
    waitingForAccount,
    breachPreview,
    isFunded,
    hasHelpCenter,
    basePath,
    navigate,
    activationDeadline,
    handleStartTrading,
    handleGetFunded,
    handleActivate,
    handleBuyActivation,
    handleVerify,
    handleContactSupport,
    clearPendingReport,
    support,
    pendingReport,
  ]);

  if (CHECKOUT_STATUS_PATH.test(pathname)) {
    return (
      <CheckoutStatusPage
        checkoutId={pathname.split("/").pop()}
        onBack={() => navigate("/portfolio")}
        onPaid={(kind) => {
          if (kind === "activation") setAccountStatus("activated");
        }}
        onNavigate={navigate}
        navItems={routes}
        activePath="/portfolio"
      />
    );
  }

  if (checkoutMode) {
    const handleCheckoutNavigate = (path) => {
      setCheckoutMode(null);
      navigate(path);
    };
    return (
      <CheckoutPage
        onBack={handleCheckoutBack}
        onComplete={handleCheckoutComplete}
        onCryptoStarted={(id) => handleCheckoutNavigate(`/checkout/status/${id}`)}
        onNavigate={handleCheckoutNavigate}
        navItems={routes}
        activePath={pathname}
        activation={checkoutMode !== "funded"}
        activationDeadline={checkoutMode === "activation" ? activationDeadline : undefined}
        theme={effectiveTheme}
      />
    );
  }

  return (
    <div className={styles.appShell}>
      <div className={styles.pageFrame}>
        <PortalHeader activePath={basePath} navItems={routes} onNavigate={navigate} effectiveTheme={effectiveTheme} toggleTheme={toggleTheme} />
        <div className={styles.pageContent}>{page}</div>
        <PortalFooter
          onNavigate={navigate}
          isFunded={isFunded}
          onGetFunded={handleGetFunded}
          cta={footerCta}
        />
      </div>
      {SHOW_STATE_SWITCHER && (
        <DevStateSwitcher
          value={accountStatus}
          options={ACCOUNT_STATUSES}
          onChange={setAccountStatus}
        />
      )}
      {SHOW_STATE_SWITCHER && ["funded", "established"].includes(accountStatus) && pathname !== "/referrals" && (
        <DevStateSwitcher
          label="PAYOUT"
          value={payoutState}
          options={PAYOUT_STATES}
          onChange={setPayoutState}
          stacked
        />
      )}
      {kycOpen && (
        <KycModal
          getAccessToken={kyc.getAccessToken}
          theme={effectiveTheme}
          onStatusChange={refreshKyc}
          onClose={handleKycClose}
        />
      )}
      {SHOW_STATE_SWITCHER && accountStatus === "activated" && pathname === "/portfolio" && (
        <DevStateSwitcher
          label="KYC"
          value={kycState}
          options={KYC_STATES}
          onChange={setKycOverride}
          stacked
        />
      )}
      {SHOW_STATE_SWITCHER && ["failed", "exhausted"].includes(accountStatus) && pathname === "/portfolio" && (
        <DevStateSwitcher
          label="BREACH"
          value={breachPreview}
          options={Object.keys(BREACH_PREVIEWS)}
          onChange={setBreachPreview}
          stacked
        />
      )}
    </div>
  );
}
